"use client";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { createIngredient } from "@/app/actions/ingredients/createIngredient";
import { Ingredient } from "@/lib/types";
import { trackEvent } from "@/lib/analytics";
import { useMutation } from "@tanstack/react-query";
import * as Sentry from "@sentry/browser";
import { toast } from "sonner";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
const duplicateIngredientSchema = z.object({
  name: z
    .string()
    .min(1, "Ingredient name is required")
    .max(100, "Ingredient name must be less than 100 characters"),
});
type DuplicateIngredientFormData = z.infer<typeof duplicateIngredientSchema>;
export function DuplicateIngredientDialog({
  ingredient,
  children,
  triggerNativeButton,
}: {
  ingredient: Ingredient;
  children: React.ReactNode;
  triggerNativeButton?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const form = useForm<DuplicateIngredientFormData>({
    resolver: zodResolver(duplicateIngredientSchema),
    defaultValues: {
      name: ingredient.name,
    },
  });
  const duplicateIngredientMutation = useMutation({
    mutationFn: async (data: DuplicateIngredientFormData) => {
      const {
        id: _id,
        createdAt: _ca,
        createdBy: _cb,
        ...creationParams
      } = ingredient;
      return createIngredient({
        data: {
          ...creationParams,
          name: data.name,
        },
      });
    },
    onSuccess: (data) => {
      toast.success("Ingredient duplicated", {
        description: data.name,
      });
      trackEvent("ingredient-detail-duplicate", {
        ["name"]: ingredient.name,
      });
      setOpen(false);
      form.reset({ name: ingredient.name });
      router.navigate({ to: `/ingredients/${data.slug}` });
    },
    onError: (error) => {
      toast.error("Failed to duplicate ingredient", {
        description: error instanceof Error ? error.message : undefined,
      });
      Sentry.captureException(error);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (isOpen) {
          form.reset({ name: ingredient.name });
        }
      }}
    >
      <DialogTrigger
        render={children}
        nativeButton={triggerNativeButton}
      ></DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Duplicate ingredient</DialogTitle>
          <DialogDescription>
            Create a copy of <strong>{ingredient.name}</strong> in your personal
            ingredient database.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((data) =>
              duplicateIngredientMutation.mutate(data),
            )}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ingredient name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter ingredient name"
                      autoFocus
                      onFocus={(event) => event.target.select()}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <DialogClose render={<Button type="button" variant="outline" />}>
                Cancel
              </DialogClose>
              <Button
                type="submit"
                busy={duplicateIngredientMutation.isPending}
              >
                Duplicate
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
